import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QrCode, Bell, Check, ArrowRight, Play, Wifi } from 'lucide-react';
import { Toaster, toast } from 'sonner';

const API_BASE =
  window.location.hostname === 'localhost'
    ? 'http://localhost:8081/api/v1'
    : '/api/v1';

function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

/* La firma de la página: el panel de turnos + el móvil que recibe el aviso.
   «Probar aviso» hace avanzar el turno y dispara la notificación en el móvil. */
function TurnoDemo() {
  const [turno, setTurno] = useState(46);
  const [notif, setNotif] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  const avisar = () => {
    if (notif) return;
    setTurno(t => t + 1);
    setNotif(true);
    if (navigator.vibrate) navigator.vibrate([80, 40, 80]);
    timer.current = setTimeout(() => setNotif(false), 3200);
  };
  return (
    <div className="relative mx-auto w-full max-w-[360px] select-none">
      {/* panel de turnos */}
      <div className="rounded-xl border p-5 text-center" style={{ background: 'var(--qr-surface)', borderColor: 'var(--qr-border)' }}>
        <p className="font-mono text-[10px] font-bold tracking-[0.22em] uppercase" style={{ color: 'var(--qr-text-secondary)' }}>
          Ahora sirviendo
        </p>
        <p className="font-mono text-[76px] font-bold leading-none tabular-nums mt-1" style={{ color: 'var(--qr-text)' }}>
          {String(turno).padStart(2, '0')}
        </p>
        <div className="flex items-center justify-center gap-2 mt-2 font-mono text-[10px] tracking-[0.16em] uppercase" style={{ color: 'var(--qr-success)' }}>
          <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: 'var(--qr-success)' }} />
          Cola en directo
        </div>
      </div>
      {/* el móvil del cliente */}
      <div className="relative mx-auto -mt-3 w-[210px] rounded-[26px] border-[6px] border-black shadow-2xl overflow-hidden"
        style={{ background: '#0b1120', transform: 'rotate(1.5deg)' }}>
        <div className="h-5 flex items-center justify-center">
          <div className="w-16 h-3 bg-black rounded-b-xl" />
        </div>
        <div className="px-3 pb-4 pt-1 h-[290px] relative">
          <p className="font-mono text-[9px] text-center tracking-[0.18em] uppercase" style={{ color: 'var(--qr-text-secondary)' }}>
            Tu turno
          </p>
          <p className="font-mono text-center text-5xl font-bold tabular-nums mt-1" style={{ color: 'var(--qr-accent)' }}>
            {String(turno + (notif ? 0 : 1)).padStart(2, '0')}
          </p>
          <p className="text-center text-[10px] mt-1" style={{ color: 'var(--qr-text-secondary)' }}>
            {notif ? '¡Te toca!' : 'Quedan 1 por delante'}
          </p>
          {/* notificación push */}
          <div
            className="absolute left-2 right-2 top-24 rounded-xl p-3 border transition-all duration-300"
            style={{
              background: '#f8fafc', borderColor: '#e2e8f0',
              opacity: notif ? 1 : 0,
              transform: notif ? 'translateY(0)' : 'translateY(-14px)',
              boxShadow: '0 12px 30px rgba(0,0,0,.45)',
            }}
            role="status"
          >
            <div className="flex items-start gap-2">
              <span className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'var(--qr-primary)' }}>
                <Bell size={14} color="#fff" />
              </span>
              <div className="min-w-0">
                <p className="text-[11px] font-bold text-slate-900 leading-tight">Tu pedido está listo</p>
                <p className="text-[10px] text-slate-500 leading-tight mt-0.5">Recoge en barra · QRHost</p>
              </div>
            </div>
          </div>
          <div className="absolute bottom-3 left-0 right-0 flex justify-center">
            <Wifi size={12} style={{ color: 'var(--qr-border)' }} />
          </div>
        </div>
      </div>
      <button
        onClick={avisar}
        className="mx-auto mt-4 flex items-center gap-2 px-5 py-2.5 rounded-lg font-mono text-[11px] font-bold tracking-[0.14em] uppercase transition-colors"
        style={{ background: 'var(--qr-primary)', color: '#fff' }}
      >
        <Bell size={14} /> Probar aviso
      </button>
    </div>
  );
}

export function LandingPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/businesses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug: slugify(form.name),
          name: form.name,
          email: form.email,
          password: form.password,
          admin_name: form.email.split('@')[0] || 'Admin',
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Error al crear la cuenta');
      }
      localStorage.setItem('qrhost_token', data.access_token);
      localStorage.setItem('qrhost_refresh_token', data.refresh_token);
      localStorage.setItem('qrhost_staff', JSON.stringify(data.staff));
      navigate('/setup');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  const tiers = [
    { name: 'Free', price: '0€', period: '', features: ['1 local', '1 staff', '100 sesiones/mes', 'Notificaciones push'] },
    { name: 'Pro', price: '9€', period: '/mes', features: ['3 locales', '5 staff', 'Ilimitado', 'Analytics', 'Soporte email'], highlighted: true },
    { name: 'Business', price: '19€', period: '/mes', features: ['10 locales', '20 staff', 'Ilimitado', 'API access', 'Soporte prioritario'] },
    { name: 'Enterprise', price: '49€', period: '/mes', features: ['Ilimitado', 'On-premise', 'SLA', 'Cuenta dedicada'] },
  ];

  const industries = [
    { name: 'Restaurantes', icon: '🍽️' },
    { name: 'Food Trucks', icon: '🚚' },
    { name: 'Food Halls', icon: '🍱' },
    { name: 'Clínicas', icon: '🏥' },
    { name: 'Peluquerías', icon: '💇' },
    { name: 'Ferias', icon: '🎪' },
  ];

  const hairline = { borderColor: 'var(--qr-border)' } as const;

  return (
    <div className="flex flex-col min-h-screen" style={{ background: 'var(--qr-bg)', color: 'var(--qr-text)' }}>
      <Toaster position="bottom-center" richColors />

      {/* nav */}
      <nav className="sticky top-0 z-40 border-b backdrop-blur-md" style={{ ...hairline, background: 'rgba(15,23,42,.88)' }}>
        <div className="max-w-6xl mx-auto px-5 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <QrCode size={20} style={{ color: 'var(--qr-accent)' }} />
            <span className="font-extrabold tracking-tight">QRHost</span>
            <span className="hidden sm:inline font-mono text-[9px] tracking-[0.18em] uppercase ml-2" style={{ color: 'var(--qr-text-secondary)' }}>
              Gestión de turnos sin app
            </span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/login" className="font-mono text-[11px] font-bold tracking-[0.12em] uppercase" style={{ color: 'var(--qr-text-secondary)' }}>
              Entrar
            </a>
            <a href="#signup" className="px-4 py-2 rounded-lg text-sm font-semibold" style={{ background: 'var(--qr-primary)', color: '#fff' }}>
              Crear cuenta
            </a>
          </div>
        </div>
      </nav>

      {/* hero: turno + móvil, sobre la escena real de la espera */}
      <section className="w-full relative overflow-hidden" style={{ isolation: 'isolate' }}>
        <img src="/landing/hero-espera.webp" alt="" aria-hidden
          className="absolute inset-0 w-full h-full object-cover object-[70%_center] -z-20" />
        <div className="absolute inset-0 -z-10"
          style={{ background: 'linear-gradient(100deg, rgba(15,23,42,.97) 38%, rgba(15,23,42,.82) 62%, rgba(15,23,42,.55))' }} />
        <div className="max-w-6xl mx-auto px-5 pt-14 pb-16 grid lg:grid-cols-[1.05fr_.95fr] gap-12 items-center">
          <div>
            <p className="font-mono text-[11px] font-bold tracking-[0.2em] uppercase mb-5" style={{ color: 'var(--qr-accent)' }}>
              Avisos de turno · sin app · sin datos
            </p>
            <h1 className="text-4xl sm:text-6xl font-extrabold leading-[1.03] tracking-tight">
              Sin app.<br />Sin datos.<br />
              <span style={{ color: 'var(--qr-accent)' }}>Sin esperas.</span>
            </h1>
            <p className="text-lg max-w-xl mt-6 leading-relaxed" style={{ color: 'var(--qr-text-secondary)' }}>
              QRHost notifica a tus clientes cuando su pedido está listo. El
              buscapersonas de toda la vida, en el móvil que ya llevan encima:
              escanean un QR y quedan en la cola.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <a
                href="/s/B5YIUI"
                className="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold"
                style={{ background: 'var(--qr-primary)', color: '#fff' }}
              >
                <Play size={17} /> Ver demo
              </a>
              <a
                href="#signup"
                className="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold border"
                style={{ ...hairline, color: 'var(--qr-text)' }}
              >
                Crear cuenta gratuita <ArrowRight size={17} />
              </a>
            </div>
            <div className="mt-8 grid gap-1.5 max-w-sm font-mono text-[11px] uppercase tracking-[0.08em]" style={{ color: 'var(--qr-text-secondary)' }}>
              {[['Setup', '2 minutos'], ['Datos personales', 'ninguno'], ['Locales', 'multi-local'], ['El cliente instala', 'nada']].map(([k, v]) => (
                <div key={k} className="flex items-baseline gap-2">
                  <span>{k}</span>
                  <span className="flex-1 border-b border-dotted translate-y-[-3px]" style={hairline} />
                  <span className="font-bold" style={{ color: 'var(--qr-text)' }}>{v}</span>
                </div>
              ))}
            </div>
          </div>
          <TurnoDemo />
        </div>
      </section>

      {/* cómo funciona: la cola, en tres turnos */}
      <section className="border-t" style={hairline}>
        <div className="max-w-6xl mx-auto px-5 py-16 w-full">
          <div className="flex items-baseline gap-4 mb-10">
            <h3 className="text-2xl font-extrabold tracking-tight">Cómo funciona</h3>
            <span className="hidden sm:block flex-1 border-t" style={hairline} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { n: '01', img: '/landing/qr-mesa.webp', alt: 'QR en la mesa de un restaurante', title: 'El cliente llega', desc: 'Escanea el QR en la mesa o recepción y se une a la cola sin descargar nada.' },
              { n: '02', img: '/landing/cola.webp', alt: 'Clientes esperando relajados con el móvil', title: 'El staff escanea', desc: 'Escaneas el QR del cliente con tu móvil para registrarlo en el sistema.' },
              { n: '03', img: '/landing/entrega.webp', alt: 'Entrega del pedido a una clienta con su móvil', title: 'Aviso instantáneo', desc: 'Cuando está listo, el cliente recibe notificación push y vibración.' },
            ].map((step) => (
              <figure key={step.n} className="m-0">
                <img src={step.img} alt={step.alt} loading="lazy" width={1024} height={768}
                  className="w-full aspect-[4/3] object-cover rounded-xl border" style={hairline} />
                <figcaption className="pt-4">
                  <p className="font-mono text-3xl font-bold tabular-nums" style={{ color: 'var(--qr-accent)' }}>{step.n}</p>
                  <h4 className="text-lg font-bold mt-2">{step.title}</h4>
                  <p className="text-sm mt-1.5 leading-relaxed" style={{ color: 'var(--qr-text-secondary)' }}>{step.desc}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* para quién es */}
      <section className="border-t" style={hairline}>
        <div className="max-w-6xl mx-auto px-5 py-12 w-full">
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
            <span className="font-mono text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: 'var(--qr-text-secondary)' }}>
              Para quién es
            </span>
            {industries.map((ind) => (
              <span key={ind.name} className="flex items-center gap-2 text-sm font-semibold">
                <span aria-hidden>{ind.icon}</span> {ind.name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* precios */}
      <section className="border-t" style={hairline}>
        <div className="max-w-6xl mx-auto px-5 py-16 w-full">
          <div className="flex items-baseline gap-4 mb-10">
            <h3 className="text-2xl font-extrabold tracking-tight">Precios</h3>
            <span className="hidden sm:block flex-1 border-t" style={hairline} />
            <span className="font-mono text-[10px] tracking-[0.14em] uppercase" style={{ color: 'var(--qr-text-secondary)' }}>
              Sin permanencia
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 rounded-xl border overflow-hidden" style={hairline}>
            {tiers.map((tier, i) => (
              <div
                key={tier.name}
                className={`flex flex-col p-6 ${i > 0 ? 'border-t sm:border-t-0 sm:border-l' : ''}`}
                style={{
                  ...hairline,
                  background: tier.highlighted ? 'var(--qr-surface)' : 'transparent',
                  boxShadow: tier.highlighted ? 'inset 0 3px 0 var(--qr-primary)' : undefined,
                }}
              >
                <div className="flex items-center justify-between">
                  <h4 className="font-mono text-[11px] font-bold tracking-[0.18em] uppercase" style={{ color: 'var(--qr-text-secondary)' }}>
                    {tier.name}
                  </h4>
                  {tier.highlighted && (
                    <span className="font-mono text-[9px] font-bold tracking-[0.12em] px-2 py-0.5 rounded uppercase" style={{ background: 'var(--qr-primary)', color: '#fff' }}>
                      Popular
                    </span>
                  )}
                </div>
                <p className="text-4xl font-extrabold tabular-nums tracking-tight mt-3">
                  {tier.price}
                  <span className="text-sm font-medium" style={{ color: 'var(--qr-text-secondary)' }}>{tier.period}</span>
                </p>
                <ul className="flex flex-col mt-4 mb-6 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-baseline gap-2.5 text-sm py-2 border-t border-dashed" style={{ ...hairline, color: 'var(--qr-text-secondary)' }}>
                      <Check size={13} className="shrink-0 translate-y-0.5" style={{ color: 'var(--qr-success)' }} />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => document.getElementById('signup')?.scrollIntoView({ behavior: 'smooth' })}
                  className="w-full py-2.5 rounded-lg font-semibold text-sm border transition-colors"
                  style={
                    tier.highlighted
                      ? { background: 'var(--qr-primary)', color: '#fff', borderColor: 'var(--qr-primary)' }
                      : { ...hairline, color: 'var(--qr-text)' }
                  }
                >
                  Empezar
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* alta */}
      <section id="signup" className="border-t" style={hairline}>
        <div className="max-w-md mx-auto px-5 py-16 w-full">
          <div className="p-6 rounded-xl border space-y-4" style={{ ...hairline, background: 'var(--qr-surface)' }}>
            <div className="flex items-baseline gap-3">
              <h3 className="text-xl font-extrabold tracking-tight">Crear cuenta</h3>
              <span className="flex-1 border-b border-dotted" style={hairline} />
              <span className="font-mono text-[10px] font-bold tracking-[0.14em] uppercase" style={{ color: 'var(--qr-success)' }}>Gratis</span>
            </div>
            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              {([
                ['text', 'Nombre del negocio', 'name', 2],
                ['email', 'Email', 'email', undefined],
                ['password', 'Contraseña', 'password', 8],
              ] as const).map(([type, ph, key, min]) => (
                <input
                  key={key}
                  type={type}
                  required
                  minLength={min}
                  placeholder={ph}
                  value={form[key]}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                  className="w-full px-4 py-3 rounded-lg outline-none border focus:border-blue-500"
                  style={{ background: 'var(--qr-bg)', borderColor: 'var(--qr-border)', color: 'var(--qr-text)' }}
                />
              ))}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-lg font-bold disabled:opacity-60"
                style={{ background: 'var(--qr-primary)', color: '#fff' }}
              >
                {loading ? 'Creando...' : 'Crear cuenta gratis'}
              </button>
            </form>
            <p className="text-xs text-center" style={{ color: 'var(--qr-text-secondary)' }}>
              ¿Ya tienes cuenta?{' '}
              <a href="/login" className="underline hover:no-underline" style={{ color: 'var(--qr-accent)' }}>
                Iniciar sesión
              </a>
            </p>
          </div>
        </div>
      </section>

      <footer className="border-t py-8" style={hairline}>
        <div className="max-w-6xl mx-auto px-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm" style={{ color: 'var(--qr-text-secondary)' }}>
          <div className="flex items-center gap-2">
            <QrCode size={15} style={{ color: 'var(--qr-accent)' }} />
            <span className="font-bold" style={{ color: 'var(--qr-text)' }}>QRHost</span>
            <span className="font-mono text-[10px] tracking-[0.14em] uppercase ml-1">© 2026 · Ibero TI</span>
          </div>
          <div className="flex gap-5 font-mono text-[11px] font-bold tracking-[0.1em] uppercase">
            <a href="/" className="hover:underline">Producto</a>
            <a href="#signup" className="hover:underline">Precios</a>
            <a href="mailto:info@iberoti.com" className="hover:underline">Contacto</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
